import React, { useEffect, useState } from 'react';
import { teamAPI, userAPI } from '../services/api';
import useAuthStore from '../store/authStore';
import Layout from '../components/Layout';
import Avatar from '../components/Avatar';
import './TeamManagement.css';

const Icon = ({ name, size = 18 }) => {
  const icons = {
    users: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-3-3.87"></path><path d="M9 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>,
    briefcase: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path></svg>,
    plus: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>,
    trash: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>,
    edit: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>,
    x: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>,
    info: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
  };
  return icons[name] || null;
};

const TeamManagement = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [memberData, setMemberData] = useState({ userId: '', role: 'Developer' });
  const [submitting, setSubmitting] = useState(false);

  const user = useAuthStore((state) => state.user);

  useEffect(() => {
    loadTeams();
  }, []);

  const loadTeams = async () => {
    try {
      setLoading(true);
      const response = await teamAPI.getAll();
      setTeams(response.data.data || []);
    } catch (error) {
      console.error('Failed to load teams:', error);
      setTeams([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    try {
      setSubmitting(true);
      await teamAPI.create(formData);
      setShowCreateModal(false);
      setFormData({ name: '', description: '' });
      loadTeams();
    } catch (error) {
      alert('Failed to create team: ' + (error.response?.data?.message || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!memberData.userId || !memberData.role) return;

    try {
      setSubmitting(true);
      await teamAPI.addMember(selectedTeam._id, memberData);
      setShowAddMemberModal(false);
      setMemberData({ userId: '', role: 'Developer' });
      setSelectedTeam(null);
      loadTeams();
    } catch (error) {
      alert('Failed to add member: ' + (error.response?.data?.message || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveMember = async (teamId, userId) => {
    if (!window.confirm('Are you sure you want to remove this member?')) return;

    try {
      await teamAPI.removeMember(teamId, userId);
      loadTeams();
    } catch (error) {
      alert('Failed to remove member: ' + (error.response?.data?.message || error.message));
    }
  };

  const openAddMemberModal = async (team) => {
    try {
      setSelectedTeam(team);
      const response = await teamAPI.getAvailableUsers(team._id);
      setAvailableUsers(response.data.data || []);
      setShowAddMemberModal(true);
    } catch (error) {
      alert('Failed to load available users: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Are you sure you want to delete this team?')) return;

    try {
      await teamAPI.delete(teamId);
      loadTeams();
    } catch (error) {
      alert('Failed to delete team: ' + (error.response?.data?.message || error.message));
    }
  };

  if (user?.role !== 'Project Manager' && user?.role !== 'Operations Head') {
    return (
      <Layout>
        <div className="access-denied-container">
          <div className="access-denied-card">
            <div className="denied-icon">🔒</div>
            <h2>Access Denied</h2>
            <p>Only Project Managers and Operations Heads can access team management.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="tm-page-container">
        <header className="tm-header">
          <div className="tm-header-text">
            <h1 className="tm-title">Team Management</h1>
            <p className="tm-subtitle">Build and scale your professional project teams</p>
          </div>
          <button className="tm-btn-primary" onClick={() => setShowCreateModal(true)}>
            <Icon name="plus" />
            <span>Create Team</span>
          </button>
        </header>

        {loading ? (
          <div className="tm-loading">
            <div className="tm-spinner"></div>
            <p>Gathering team data...</p>
          </div>
        ) : teams.length === 0 ? (
          <div className="tm-empty-state">
            <div className="empty-illustration">👥</div>
            <h3>No Teams Yet</h3>
            <p>Start by creating your first team to manage project collaborators.</p>
            <button className="tm-btn-outline" onClick={() => setShowCreateModal(true)}>
              Create First Team
            </button>
          </div>
        ) : (
          <div className="tm-grid">
            {teams.map(team => (
              <div key={team._id} className="tm-team-card">
                <div className="team-card-inner">
                  <div className="team-card-header">
                    <div className="team-info-group">
                      <h3 className="team-name">{team.name}</h3>
                      <p className="team-bio">{team.description || 'No description provided'}</p>
                    </div>
                    <div className="team-header-actions">
                      <button className="icon-btn danger" onClick={() => handleDeleteTeam(team._id)} title="Delete Team">
                        <Icon name="trash" size={16} />
                      </button>
                    </div>
                  </div>

                  <div className="team-card-stats">
                    <div className="stat-pill">
                      <Icon name="users" size={14} />
                      <span>{team.members?.length || 0} Members</span>
                    </div>
                    <div className="stat-pill">
                      <Icon name="briefcase" size={14} />
                      <span>{team.projects?.length || 0} Projects</span>
                    </div>
                  </div>

                  <div className="team-members-section">
                    <div className="section-title-bar">
                      <h4>Active Members</h4>
                      <button className="add-text-btn" onClick={() => openAddMemberModal(team)}>
                        <Icon name="plus" size={14} />
                        Add Member
                      </button>
                    </div>

                    <div className="members-compact-list">
                      {team.members && team.members.length > 0 ? (
                        team.members.map(member => (
                          <div key={member.user._id} className="member-row">
                            <div className="member-main">
                              <Avatar
                                name={member.user.name}
                                profileImage={member.user.profileImage}
                                size="small"
                              />
                              <div className="member-meta">
                                <span className="member-name">{member.user.name}</span>
                                <span className="member-role-tag">{member.role}</span>
                              </div>
                            </div>
                            <button
                              className="member-remove-btn"
                              onClick={() => handleRemoveMember(team._id, member.user._id)}
                              title="Remove"
                            >
                              <Icon name="x" size={14} />
                            </button>
                          </div>
                        ))
                      ) : (
                        <div className="inline-empty">
                          <Icon name="info" size={14} />
                          <span>No members assigned yet</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Improved Create Team Modal */}
        {showCreateModal && (
          <div className="tm-modal-overlay" onClick={() => setShowCreateModal(false)}>
            <div className="tm-modal" onClick={(e) => e.stopPropagation()}>
              <div className="tm-modal-header">
                <h2>New Team</h2>
                <button className="modal-close" onClick={() => setShowCreateModal(false)}><Icon name="x" /></button>
              </div>
              <form onSubmit={handleCreateTeam} className="tm-form">
                <div className="tm-form-group">
                  <label>Team Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g. Frontend Squad"
                    required
                  />
                </div>
                <div className="tm-form-group">
                  <label>Vision / Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="What is this team's focus?"
                    rows="3"
                  />
                </div>
                <div className="tm-modal-footer">
                  <button type="button" className="btn-tm-ghost" onClick={() => setShowCreateModal(false)}>Cancel</button>
                  <button type="submit" className="tm-btn-primary" disabled={submitting}>
                    {submitting ? 'Creating...' : 'Create Team'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Improved Add Member Modal */}
        {showAddMemberModal && (
          <div className="tm-modal-overlay" onClick={() => setShowAddMemberModal(false)}>
            <div className="tm-modal" onClick={(e) => e.stopPropagation()}>
              <div className="tm-modal-header">
                <h2>Add to {selectedTeam?.name}</h2>
                <button className="modal-close" onClick={() => setShowAddMemberModal(false)}><Icon name="x" /></button>
              </div>
              <form onSubmit={handleAddMember} className="tm-form">
                <div className="tm-form-group">
                  <label>Select Colleague</label>
                  <select
                    value={memberData.userId}
                    onChange={(e) => setMemberData({ ...memberData, userId: e.target.value })}
                    required
                  >
                    <option value="">Choose a user...</option>
                    {availableUsers.map(u => (
                      <option key={u._id} value={u._id}>{u.name} ({u.role})</option>
                    ))}
                  </select>
                </div>
                <div className="tm-form-group">
                  <label>Assign Team Role</label>
                  <select
                    value={memberData.role}
                    onChange={(e) => setMemberData({ ...memberData, role: e.target.value })}
                    required
                  >
                    <option value="Project Lead">Project Lead</option>
                    <option value="Developer">Developer</option>
                    <option value="QA Engineer">QA Engineer</option>
                    <option value="UI Engineer">UI Engineer</option>
                  </select>
                </div>
                <div className="tm-modal-footer">
                  <button type="button" className="btn-tm-ghost" onClick={() => setShowAddMemberModal(false)}>Cancel</button>
                  <button type="submit" className="tm-btn-primary" disabled={submitting}>
                    {submitting ? 'Adding...' : 'Add Member'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TeamManagement;
